import { ThumbsUp } from 'lucide-react'
import { UpvotesSpaceFields } from '@/components/features/upvotes/UpvotesSpaceFields'
import { defaultUpvotesConfig } from '@/components/features/upvotes/upvoteUtils'
import { sdk } from '@/lib/sdk'
import type { ModuleDefinition } from '../types'
import { lazyPage } from '@/lib/lazyPage'

const UpvotesPage = lazyPage(() => import('@/pages/Upvotes'), 'UpvotesPage')
const UpvoteTopicPage = lazyPage(() => import('@/pages/UpvoteTopicDetails'), 'UpvoteTopicPage')

interface BoardConfig {
  requireApproval: boolean
  /** What the server holds (edit); absent on create, where the space is born without approval. */
  savedRequireApproval?: boolean
}

/**
 * F-139 Upvote: one space is one board (no "one or many"). Creating it asks only
 * the common fields plus prior approval, which is short: on the details step
 * (PADROES-UI section 14), saved by the board's own endpoint.
 */
export const upvotesModule: ModuleDefinition = {
  key: 'upvotes',
  icon: ThumbsUp,
  iconName: 'thumbs-up',
  namespaces: ['upvotes'],
  ready: true,
  ListPage: UpvotesPage,
  renderDetail: ({ spaceData, params }) => <UpvoteTopicPage spaceData={spaceData} topicId={params.itemId} />,
  setup: {
    wizardStep: 'details',
    defaultConfig: () => defaultUpvotesConfig(),
    validate: () => null,
    Fields: UpvotesSpaceFields,
    persist: {
      type: 'endpoint',
      /* Only a change is sent: a new board is born without approval. */
      save: async (spaceId, config: BoardConfig) => {
        if (Boolean(config?.requireApproval) === Boolean(config?.savedRequireApproval)) return null
        return sdk.upvotes.updateBoard(spaceId, { requireApproval: Boolean(config.requireApproval) })
      },
      load: async (spaceId) => {
        const board = await sdk.upvotes.getBoard(spaceId)
        if (!board.canManage) return null
        return { requireApproval: board.requireApproval, savedRequireApproval: board.requireApproval }
      }
    }
  },
  workflow: { trigger: { block: 'upvotes', families: ['upvote'] } }
}
